import type { PagePaginationFragment } from "../client/generated";
import { Input } from "@acdc2/ui/components/input";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { routingPaths } from "../navigation/constants";
import { useNavigate } from "react-router";
import { makePageThumbnailID } from "../regions/utils";

type Props = {
  fragment: PagePaginationFragment;
};

export function PagePagination({ fragment }: Props) {
  const { t } = useTranslation();
  const [value, setValue] = useState(fragment.index);
  const navigate = useNavigate();

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;

    const targetPageNumber = parseInt(event.currentTarget.value);
    const targetPage = fragment.document.pages.pages.find(
      (page) => page.index === targetPageNumber,
    );

    if (!targetPage) return;
    navigate(routingPaths.documentPage(fragment.document.id, targetPage.id));
    setTimeout(() => {
      document
        .getElementById(makePageThumbnailID(targetPage.id))
        ?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
    });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(parseInt(e.target.value));
  };

  const onBlur = () => {
    setValue(fragment.index);
  };

  return (
    <div className="flex p-2 gap-2 justify-center items-center">
      <div>
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          max={fragment.document.pages.total}
          className="[appearance:textfield] text-center"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          size={fragment.document.pages.total.toString().length}
        />
      </div>
      <span>{t("PagePagination.label")}</span>
      <div>
        <Input
          disabled
          defaultValue={fragment.document.pages.total}
          className="text-center"
          size={fragment.document.pages.total.toString().length}
        />
      </div>
    </div>
  );
}
